import { setChartTheme } from "@components/charts/Charts.types";
import { IDashboardDefinition } from "@components/dashboard";
import { TilePadding } from "@components/dashboard/Dashboard.styles";
import { IHeaderIcon } from "@components/header/Header";
import { HOTSPOT_VIEW_ATTR } from "@components/hotspots/Hotspots.utils";
import { HotspotViewIds } from "@components/hotspots/HotspotViewIds";
import { withTheme } from "@hoc/withTheme";
import { EntityTypeName } from "@odata/GeneratedEntityTypes";
import { getEnumNameSpaceName } from "@odata/GeneratedEnums.utils";
import { forEachKey } from "@utils/general";
import KeyboardShortcutsManager, { KeyboardShortcut } from "@utils/keyboardShortcutsManager";
import React from "react";
import { WithTranslation, withTranslation } from "react-i18next";

import { Button } from "../../components/button";
import { CustomerCompanySelector } from "../../components/navigation/CompanySelector";
import DashboardBackground from "../../components/navigation/DashboardBackground";
import { ScrollBar } from "../../components/scrollBar";
import { AppContext, ContextEvents, IAppContext } from "../../contexts/appContext/AppContext.types";
import { WithPermissionContext, withPermissionContext } from "../../contexts/permissionContext/withPermissionContext";
import { TRecordType } from "../../global.types";
import TestIds from "../../testIds";
import { PropsWithTheme } from "../../theme";
import { ExtendedShellContent } from "../../views/main/Shell.types";
import { VIEW_PADDING_VALUE } from "../../views/View.styles";
import { getDashboardDefinition as getAgendaDef } from "./AgendaDashboardDef";
import CloseCustomerPortalButton from "./CloseCustomerPortalButton";
import { getDashboardDefinition as getCustomerDef } from "./CustomerDashboardDef";
import { DashboardManagerFactory, IDashboardManager, removeAllSavedDashboards } from "./DashboardManager";
import {
    CloseButton,
    HomeEditButtonFooter,
    HomeSmartHeaderStyled,
    StyledHome,
    StyledHomeBackground
} from "./Home.style";
import { getVisibleDashboards, isInCustomerPortal } from "./Home.utils";
import HomeDashboard from "./HomeDashboard";
import { getDashboardDefinition as getTenantDef } from "./TenantDashboardDef";

export const HomeDashboards: TRecordType<() => IDashboardDefinition> = {
    Agenda: getAgendaDef,
    Customer: getCustomerDef,
    Tenant: getTenantDef,
};

interface IProps extends WithTranslation, PropsWithTheme, WithPermissionContext {
}

interface IState {
    isLoading?: boolean;
    managers?: IDashboardManager[];
    inEditMode?: boolean;
}

class Home extends React.PureComponent<IProps, IState> {
    static contextType = AppContext;
    //sadly, breaks typescript type checking
    //context: React.ContextType<typeof AppContext>;

    _isMounted = false;

    _unsubscribeKeyboardShortcuts: () => void;

    state: IState = {
        isLoading: true,
        managers: []
    };

    constructor(props: IProps, context: IAppContext) {
        super(props);

        context.eventEmitter.on(ContextEvents.ExtendedShellHidden, this.handleShellClosed);

        // todo: move to chart provider or something like that
        setChartTheme(props.theme);

        this._unsubscribeKeyboardShortcuts = KeyboardShortcutsManager.subscribe({
            shortcuts: [{ id: KeyboardShortcut.ALT_X }, { id: KeyboardShortcut.ALT_S }, { id: KeyboardShortcut.ALT_C }, { id: KeyboardShortcut.ALT_SHIFT_X }],
            callback: this.handleKeyboardShortcut
        });
    }

    componentDidMount() {
        this._isMounted = true;

        if (this.props.tReady) {
            this.loadDashboards();
        }
    }

    componentDidUpdate(prevProps: Readonly<IProps>) {
        if (!prevProps.tReady && this.props.tReady) {
            this.loadDashboards();
        }
        const permissionsChanged = this.props.permissionContext.companyPermissions !== prevProps.permissionContext.companyPermissions;
        if (permissionsChanged) {
            this.state.managers?.forEach(manager => manager.refreshConfig());
        }
    }

    componentWillUnmount() {
        this._isMounted = false;
        this.context.eventEmitter.off(ContextEvents.ExtendedShellHidden, this.handleSettingsDone);
        this._unsubscribeKeyboardShortcuts?.();
    }

    get headerIcons(): IHeaderIcon[] {
        return [{
            id: "Settings",
            onClick: this.handleSettingsIconClick,
            label: this.props.t("Home:Settings"),
            iconName: "Settings"
        }];
    }

    async loadDashboards() {
        const promises: Promise<IDashboardManager>[] = [];
        forEachKey(HomeDashboards, (id) => {
            promises.push(DashboardManagerFactory(id, HomeDashboards[id], this.context, this.props.permissionContext));
        });

        if (this.context.getCompanyId()) {
            /* Company related dashboard needs to await FYs
            (when switching from tenant to particular company customer dashboard) */
            await this.context.getFYPromise();
        }

        const managers = await Promise.all(promises);

        if (this._isMounted) {
            this.setState({
                isLoading: false,
                managers
            });
        }
    }

    handleKeyboardShortcut = (shortcut: KeyboardShortcut, event: KeyboardEvent): boolean => {
        switch (shortcut) {
            case KeyboardShortcut.ALT_SHIFT_X:
                // remove saved state of all dashboards
                removeAllSavedDashboards(this.context);
                break;
            case KeyboardShortcut.ALT_C:
                // open dashboard configuration
                this.handleSettingsIconClick();
                break;
            case KeyboardShortcut.ALT_S:
                this.handleSettingsDone();
                break;
            case KeyboardShortcut.ALT_X:
                this.handleSettingsCancel();
                break;
        }

        return false;
    }

    handleSettingsIconClick = () => {
        if (!this.state.inEditMode) {
            getVisibleDashboards(this.state.managers, this.context)
                .forEach(manager => manager.setOriginalConfig());

            this.context.openExtendedShell(ExtendedShellContent.DashboardSettings);
            this.setState({ inEditMode: true });
        }
    };

    handleShellClosed = () => {
        // todo: possibly also save settings? Or dashboard config is applied immediately?
        this.setState({ inEditMode: false });
    };

    handleSettingsCancel = () => {
        if (this.state.inEditMode) {
            getVisibleDashboards(this.state.managers, this.context)
                    .forEach(manager => manager.restoreOriginalConfig());

            this.context.openExtendedShell(null);
            this.setState({ inEditMode: false });
        }
    };


    handleSettingsDone = () => {
        if (this.state.inEditMode) {
            getVisibleDashboards(this.state.managers, this.context)
                    .forEach(manager => manager.sync());

            this.context.openExtendedShell(null);
            this.handleShellClosed();
        }
    };

    renderCustomerPortalHeader(): React.ReactNode {
        return (<>
            <HomeSmartHeaderStyled customHeaderContent={<CustomerCompanySelector/>}
                                   hotspotId={"DASHBOARD_HEADER"}
                                   icons={this.headerIcons}
                                   shouldHideVariant
                                   hideBreadcrumbs/>
            <CloseCustomerPortalButton/>
        </>);
    }

    renderHeader(): React.ReactNode {
        return (
            <HomeSmartHeaderStyled title={this.props.t("Home:Title")}
                                   hotspotId={"DASHBOARD_HEADER"}
                                   icons={this.headerIcons}
                                   shouldHideVariant
                                   hideBreadcrumbs/>
        );
    }

    renderDashboardPage(): React.ReactNode {
        const visibleDashboards = getVisibleDashboards(this.state.managers, this.context);
        const _isInCustomerPortal = isInCustomerPortal();

        return (
            <StyledHomeBackground {...{ [HOTSPOT_VIEW_ATTR]: HotspotViewIds.HomeView }} data-testid={TestIds.View}>
                {_isInCustomerPortal ? this.renderCustomerPortalHeader() : this.renderHeader()}
                <StyledHome $inEditMode={this.state.inEditMode}>
                    <ScrollBar primary style={{
                        padding: `${TilePadding}px ${VIEW_PADDING_VALUE}px ${TilePadding}px`
                    }}>
                        {visibleDashboards.map(manager => (
                            <HomeDashboard key={manager.name}
                                           manager={manager}
                                           showHeader={visibleDashboards.length > 1}
                                           inEditMode={this.state.inEditMode}/>
                        ))}
                    </ScrollBar>
                </StyledHome>
                {this.state.inEditMode && (
                    <HomeEditButtonFooter>
                        <CloseButton onClick={this.handleSettingsCancel}
                                isLight ignoreTheme>
                            {this.props.t("Common:General.Cancel")}
                        </CloseButton>

                        <Button onClick={this.handleSettingsDone}
                                isLight ignoreTheme>
                            {this.props.t("Home:Done")}
                        </Button>
                    </HomeEditButtonFooter>
                )}
                {/* isOverContent means that it's positioned over StyledHomeBackground, but the actual
                  content - StyledHome - is positioned even higher */}
                <DashboardBackground isOverContent/>
            </StyledHomeBackground>
        );
    }

    render() {
        if (!this.props.tReady) {
            return null;
        }
        return this.renderDashboardPage();
    }
}

export default withTranslation([
    "Home", "Common",
    getEnumNameSpaceName(EntityTypeName.Accounting),
    getEnumNameSpaceName(EntityTypeName.ElectronicSubmissionType)]
)(withTheme(withPermissionContext(Home)));